import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxfWatchlistV1OfacSdnQueryServiceOfacSdnGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxfWatchlistV1OfacSdnState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { OfacSdnEventDrawer, OfacSdnEventSummary } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdn-event';
import { getOfacSdnStateListItems } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdn-list-items';
import { getOfacSdnDetailPath } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdn-routing';
import { WatchlistOfacSdnStatusPill } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdn-status-pill';
import { WatchlistAddressesTable } from '../../../../../../../../../../features/watchlist/watchlist-addresses-table';
import { WatchlistBirthDatesTable } from '../../../../../../../../../../features/watchlist/watchlist-birth-dates-table';
import { WatchlistCitizenshipsTable } from '../../../../../../../../../../features/watchlist/watchlist-citizenships-table';
import { WatchlistIdsTable } from '../../../../../../../../../../features/watchlist/watchlist-ids-table';
import { WatchlistIndividualAkasTable } from '../../../../../../../../../../features/watchlist/watchlist-individual-akas-table';
import { WatchlistNationalitiesTable } from '../../../../../../../../../../features/watchlist/watchlist-nationalities-table';
import { WatchlistOrganizationAkasTable } from '../../../../../../../../../../features/watchlist/watchlist-organization-akas-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/ofac/sdn/_sdn/$ofacSdnId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ofac-sdn-${params.ofacSdnId}`,
        label: 'SDN',
        to: getOfacSdnDetailPath(params.ofacSdnId),
        suffix: <CopyButton content={params.ofacSdnId} label="Copy OFAC SDN ID" tooltip={{ copy: params.ofacSdnId, copied: 'Copied OFAC SDN ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('OFAC SDN'),
  component: OfacSdnDetail,
});

function getMenuItems(ofacSdnId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: OfacSdnEventDrawer,
        props: { request: { ofacSdnId } },
      },
    },
    actions: undefined,
  };
}

function getListItems(data: IxfWatchlistV1OfacSdnState | undefined) {
  const items = getOfacSdnStateListItems(data);

  return {
    left: [
      items.data.spec.fileDate,
      items.data.spec.ofacUid,
      items.data.spec.sdn.typeName,
      ...match(items.data.spec.sdn)
        .with({ individual: P.not(P.nullish) }, ({ individual }) => [
          individual.value.firstName,
          individual.value.lastName,
          individual.value.programs,
          individual.value.remarks,
        ])
        .with({ organization: P.not(P.nullish) }, ({ organization }) => [
          organization.value.name,
          organization.value.programs,
          organization.value.remarks,
        ])
        .otherwise(() => []),
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.ofacSdnId, items.ofacFileId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load OFAC SDN', 'view this OFAC SDN');

function OfacSdnDetail() {
  const { ofacSdnId } = Route.useParams();
  const request = { ofacSdnId } as const;
  const { data, error, isPending } = useIxfWatchlistV1OfacSdnQueryServiceOfacSdnGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.ofacSdn), [data?.ofacSdn]);
  const menuItems = useMemo(() => getMenuItems(ofacSdnId), [ofacSdnId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistOfacSdnStatusPill isPending={showPending} size="large" status={data?.ofacSdn?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <OfacSdnEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      {/* Individual fields */}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Aliases</Typography.Level3>
            <WatchlistIndividualAkasTable akas={data.ofacSdn.data.spec.sdn.individual.akas ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Birth Dates</Typography.Level3>
            <WatchlistBirthDatesTable birthDates={data.ofacSdn.data.spec.sdn.individual.birthDates ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Addresses</Typography.Level3>
            <WatchlistAddressesTable addresses={data.ofacSdn.data.spec.sdn.individual.addresses ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Nationalities</Typography.Level3>
            <WatchlistNationalitiesTable
              loadingRows={2}
              nationalities={data.ofacSdn.data.spec.sdn.individual.nationalities ?? STABLE_EMPTY_ARRAY_REF}
            />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Citizenships</Typography.Level3>
            <WatchlistCitizenshipsTable citizenships={data.ofacSdn.data.spec.sdn.individual.citizenships ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'individual' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>IDs</Typography.Level3>
            <WatchlistIdsTable ids={data.ofacSdn.data.spec.sdn.individual.ids ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {/* Organization fields */}

      {data?.ofacSdn?.data?.spec?.sdn && 'organization' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Aliases</Typography.Level3>
            <WatchlistOrganizationAkasTable akas={data.ofacSdn.data.spec.sdn.organization.akas ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'organization' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Addresses</Typography.Level3>
            <WatchlistAddressesTable addresses={data.ofacSdn.data.spec.sdn.organization.addresses ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.ofacSdn?.data?.spec?.sdn && 'organization' in data.ofacSdn.data.spec.sdn && data.ofacSdn.data.spec.sdn.organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>IDs</Typography.Level3>
            <WatchlistIdsTable ids={data.ofacSdn.data.spec.sdn.organization.ids ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
